import { track } from "@/lib/analytics";
import apiRequest from "@/lib/axios";
import { getDeviceKind } from "@/lib/device";
import {
  CasinoCategoryItem,
  Game,
  GameItemsResponse,
  GamePageData,
  LobbySection,
  Option,
} from "@/types";

import { DEFAULT_GAME_LIST_LIMIT, PROVIDER_GAMES_FETCH_LIMIT } from "./data";
import { GameListFilters } from "./type";

/** The public page of a provider game; fails when the site has no such page. */
export const getGamePage = async (provider: string, gameId: string): Promise<GamePageData> =>
  apiRequest<GamePageData>({
    method: "GET",
    url: "game/page",
    params: { provider, gameId },
    errorMessage: "Failed to load game",
  });

export const getGameById = async (id: string): Promise<Game> => {
  const res = await apiRequest<{ game: Game }>({
    method: "GET",
    url: "game/by-id",
    params: { id },
    errorMessage: "Failed to load game",
  });

  return res.game;
};

// `redirect`: the provider's page is opened as a whole page, not inside our frame (LG).
export type OpenGameResult = { url: string; redirect: boolean } | { options: string[] };

export const openGame = async (
  id: string,
  isMobile = false,
  fun = false,
  playCurrency?: string,
): Promise<OpenGameResult> => {
  const res = await apiRequest<{
    url?: string;
    redirect?: boolean;
    currencySelection?: { options: string[] };
  }>({
    method: "POST",
    url: fun ? "game/open-fun" : "game/open",
    data: { id, isMobile, ...(playCurrency ? { playCurrency } : {}) },
    errorMessage: "Failed to open game",
  });
  if (res.currencySelection) return { options: res.currencySelection.options };
  track("game_open", { game_id: id, mode: fun ? "fun" : "real", mobile: isMobile });

  return { url: res.url ?? "", redirect: Boolean(res.redirect) };
};

export const getRecentGames = async (limit = DEFAULT_GAME_LIST_LIMIT): Promise<Game[]> => {
  const res = await apiRequest<{ results: Game[] }>({
    method: "GET",
    url: "game/recent",
    params: { limit, device: getDeviceKind() },
    errorMessage: "Failed to load recent games",
  });

  return res.results || [];
};

export const getFavoriteGames = async (): Promise<Game[]> => {
  const res = await apiRequest<{ results: Game[] }>({
    method: "GET",
    url: "game/favorites",
    params: { device: getDeviceKind() },
    errorMessage: "Failed to load favorite games",
  });

  return res.results || [];
};

export const getFavoriteGameIds = async (): Promise<string[]> => {
  const games = await getFavoriteGames();
  return games.map((game) => game.gid);
};

export const toggleFavoriteGame = async (
  gameId: string,
): Promise<{ favorited: boolean; gameIds: string[] }> => {
  return apiRequest<{ favorited: boolean; gameIds: string[] }>({
    method: "POST",
    url: "game/favorites/toggle",
    data: { gameId },
    errorMessage: "Failed to update favorite",
  }).then((res) => {
    track("game_favorite", { game_id: gameId, favorited: res.favorited });
    return res;
  });
};

export const getGameCategories = async (): Promise<CasinoCategoryItem[]> => {
  const res = await apiRequest<{ categories: CasinoCategoryItem[] }>({
    method: "GET",
    url: "game/categories",
    errorMessage: "Failed to load categories",
  });
  return res.categories ?? [];
};

export const getGames = async (
  code: string,
  value: string,
  page = 1,
  limit = DEFAULT_GAME_LIST_LIMIT,
  provider = "",
  search = "",
  sort = "",
  bonusBuy = false,
): Promise<GameItemsResponse> => {
  return apiRequest<GameItemsResponse>({
    method: "GET",
    url: "game/list",
    params: {
      code,
      value,
      provider,
      search,
      sort,
      bonusBuy: bonusBuy ? "1" : undefined,
      page,
      limit,
      device: getDeviceKind(),
    },
    errorMessage: "Failed to load games",
  });
};

/** The header search: every game of the site whose name contains `q`, most played first. */
export const searchGames = async (q: string, page = 1, limit = 24): Promise<GameItemsResponse> =>
  apiRequest<GameItemsResponse>({
    method: "GET",
    url: "game/search",
    params: { q, page, limit, device: getDeviceKind() },
    errorMessage: "Failed to search games",
  }).then((res) => {
    // Later pages are the same search scrolled further.
    if (page === 1) track("search", { search_term: q.trim(), results: res.total });
    return res;
  });

const getGamesByProvider = async (
  provider: string,
  page = 1,
  limit = DEFAULT_GAME_LIST_LIMIT,
): Promise<GameItemsResponse> => {
  return apiRequest<GameItemsResponse>({
    method: "GET",
    url: "game/by-provider",
    params: { provider, page, limit, device: getDeviceKind() },
    errorMessage: "Failed to load games",
  });
};

export const getLobbySections = async (
  limit = DEFAULT_GAME_LIST_LIMIT,
): Promise<LobbySection[]> => {
  const res = await apiRequest<{
    sections: Array<{
      type: { _id: string; name: string; image: string };
      total: number;
      results: Game[];
    }>;
  }>({
    method: "GET",
    url: "game/types",
    params: { limit, device: getDeviceKind() },
    errorMessage: "Failed to load lobby",
  });

  return (res.sections || []).map((section) => ({
    type: section.type,
    total: section.total,
    games: section.results || [],
  }));
};

export const getProviderGameOptions = async (provider: string): Promise<Option[]> => {
  const { results } = await getGamesByProvider(provider, 1, PROVIDER_GAMES_FETCH_LIMIT);
  return results.map((game) => ({ label: game.name, value: game.gid }));
};

/** What one category, game type or provider's list can be narrowed by (same code / value as getGames). */
export const getGameListFilters = async (code: string, value: string): Promise<GameListFilters> => {
  const res = await apiRequest<Partial<GameListFilters>>({
    method: "GET",
    url: "game/list-providers",
    params: { code, value, device: getDeviceKind() },
    errorMessage: "Failed to load providers",
  });
  return { providers: res.providers ?? [], bonusBuyGames: res.bonusBuyGames ?? 0 };
};
